import { skipToken } from '@reduxjs/toolkit/query/react';
import Button from '@rio-cloud/rio-uikit/Button';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';

import { useFetchEulaStatusQuery } from '../../services/iamApi';
import { useUserAccount } from '../../configuration/login/loginSlice';
import { useRelevantAssets } from '../../services/assetsApi';
import {
    iframeMessageType,
    isRunningInIframe,
    REMIND_ME_LATER_COOKIE_NAME,
    REMIND_ME_LATER_COOKIE_VALUE,
    sendMessageToParent,
} from '../../utils';
import { useHandlePostError, useIsXsScreen, useUpdateEulaStatus } from '../../hooks/hooks';
import { useAppDispatch } from '../../configuration/setup/hooks';
import {
    showDeclineConfirmationDialog,
    showReAcceptConfirmationDialog,
    usePreviouslyDeclined,
} from '../../data/appSlice';
import { knownEulaStatus } from './config/types';
import { reportRemindMeLaterClickedToSentry } from '../../configuration/setup/sentry';

export const extractUserProfileIdFromSubject = (subject: string) => {
    const parts = subject.split(':');
    return parts.length > 1 ? parts[1] : subject;
};

export const EulaConsentFooter = ({ inBodyBottomBar }: { inBodyBottomBar?: boolean }) => {
    const dispatch = useAppDispatch();
    const accountId = useUserAccount();
    const wasPreviouslyDeclined = usePreviouslyDeclined();

    const isXsScreen = useIsXsScreen();

    const assets = useRelevantAssets();

    const { data: eulaStatus, isFetching: isEulaStatusFetching } = useFetchEulaStatusQuery(accountId ?? skipToken);
    const [updateEulaStatusInBackend, updateEulaStatusResult] = useUpdateEulaStatus(eulaStatus?.etag);
    const [checkboxChecked, setCheckboxChecked] = useState(false);

    useHandlePostError(updateEulaStatusResult.error);

    /**
     * Toggles the checkbox state when the user interacts with it.
     */
    const handleCheckbox = () => {
        setCheckboxChecked(prev => !prev);
    };

    const handleUpdateEulaStatusInBackend = () => {
        updateEulaStatusInBackend(knownEulaStatus.ACCEPTED);
    };

    /**
     * Handles acceptance of the EULA.
     * Closes the dialog and reloads the page after a short delay.
     */
    const handleAccept = () => {
        if (wasPreviouslyDeclined) {
            dispatch(showReAcceptConfirmationDialog());
        } else {
            handleUpdateEulaStatusInBackend();
        }
    };

    /**
     * Handles declination of the EULA.
     * Opens the decline confirmation dialog.
     */
    const handleDecline = () => {
        dispatch(showDeclineConfirmationDialog());
    };

    const handleRemindMeLater = async () => {
        const expiresInDays = 1;
        Cookies.set(REMIND_ME_LATER_COOKIE_NAME, REMIND_ME_LATER_COOKIE_VALUE, {
            expires: expiresInDays,
            secure: true,
            sameSite: 'strict',
        });
        await reportRemindMeLaterClickedToSentry(accountId);
        sendMessageToParent({
            type: iframeMessageType.REMIND_ME_LATER_COOKIE_FOUND,
        });
    };

    const renderRemindMeLaterButton = (options?: { hide?: boolean }) => {
        const hide = options?.hide ?? false;

        return hide ? (
            <div className={'visibility-hidden'} />
        ) : (
            <div>
                {isRunningInIframe() ? (
                    <Button
                        onClick={handleRemindMeLater}
                        disabled={updateEulaStatusResult.isLoading || isEulaStatusFetching}
                        bsStyle={Button.MUTED}
                    >
                        <FormattedMessage id={'intl-msg:man-eula.dialog.footer.remind-later'} />
                    </Button>
                ) : null}
            </div>
        );
    };

    const renderLogoutButton = () => (
        <a href={'https://auth.iam.rio.cloud/logout'} target={'_top'}>
            <Button bsStyle='muted'>
                <span className={'uikit-icon icon-logout'} />
                <FormattedMessage id={'intl-msg:man-eula.dialog.footer.logout'} />
            </Button>
        </a>
    );

    const renderCheckbox = () => (
        <Checkbox checked={checkboxChecked} onChange={handleCheckbox}>
            <FormattedMessage
                id={'intl-msg:man-eula.dialog.footer.checkbox'}
                values={{ b: chunks => <strong>{chunks}</strong> }}
            />
        </Checkbox>
    );

    const renderButtonToolbar = () => (
        <ButtonToolbar>
            <div className='position-relative'>
                {/* Tooltip for confirming authorization before accepting or declining */}
                {checkboxChecked && wasPreviouslyDeclined && (
                    <SimpleTooltip
                        content={<FormattedMessage id={'intl-msg:man-eula.dialog.footer.decline.tooltip'} />}
                        placement='top'
                        className='max-width-200'
                    >
                        <div className='position-absolute inset-0' />
                    </SimpleTooltip>
                )}
                <Button
                    bsStyle='default'
                    onClick={handleDecline}
                    disabled={
                        !checkboxChecked ||
                        updateEulaStatusResult.isLoading ||
                        isEulaStatusFetching ||
                        wasPreviouslyDeclined
                    }
                >
                    <FormattedMessage id={'intl-msg:man-eula.dialog.footer.decline'} />
                </Button>
            </div>
            <div className='position-relative'>
                {/* Tooltip for confirming authorization before accepting or declining */}
                {!checkboxChecked && (
                    <SimpleTooltip
                        content={<FormattedMessage id={'intl-msg:man-eula.dialog.footer.accept.tooltip'} />}
                        placement='top'
                        className='max-width-200'
                    >
                        <div className='position-absolute inset-0' />
                    </SimpleTooltip>
                )}
                <Button
                    bsStyle='primary'
                    onClick={handleAccept}
                    disabled={!checkboxChecked || updateEulaStatusResult.isLoading || isEulaStatusFetching}
                >
                    <FormattedMessage id={'intl-msg:man-eula.dialog.footer.accept'} />
                </Button>
            </div>
        </ButtonToolbar>
    );

    if (eulaStatus === undefined || assets === undefined) {
        // hide footer content if eulaStatus is undefined. This can happen when user does not have permissions to view the EULA
        return null;
    }

    return inBodyBottomBar ? (
        <div className={`container fluid-small width-100pct ${isXsScreen ? 'padding-x-20' : 'padding-x-50'}`}>
            <div className={`${isXsScreen ? 'padding-x-20' : 'padding-x-10'}`}>
                <div className='margin-bottom-15'>
                    <div className='alert alert-warning rounded padding-10 display-flex align-items-center gap-10'>
                        <div>
                            <span className='uikit-icon icon-warning-sign text-size-20' />
                        </div>
                        <div>
                            <FormattedMessage
                                id={'intl-msg:man-eula.dialog.body.decline-warning'}
                                values={{
                                    b: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className='width-100pct display-flex justify-content-end align-items-center gap-20'>
                    {isRunningInIframe() ? renderLogoutButton() : renderRemindMeLaterButton({ hide: true })}
                    {renderCheckbox()}
                    {renderButtonToolbar()}
                </div>
            </div>
        </div>
    ) : (
        <div className='padding-top-20'>
            <div className='flex-1-1'>
                <div className='alert alert-warning margin-bottom-10 rounded padding-10 display-flex align-items-center gap-10'>
                    <div>
                        <span className='uikit-icon icon-warning-sign text-size-20' />
                    </div>
                    <div>
                        <FormattedMessage
                            id={'intl-msg:man-eula.dialog.body.decline-warning'}
                            values={{
                                b: chunks => <strong>{chunks}</strong>,
                            }}
                        />
                    </div>
                </div>
                <div className='display-flex flex-column-reverse-xs flex-wrap justify-content-between align-items-end gap-10'>
                    {isRunningInIframe() ? renderLogoutButton() : renderRemindMeLaterButton({ hide: true })}
                    <div className='display-flex flex-column align-items-end gap-10'>
                        <div className='flex-1-1 display-flex align-items-center'>{renderCheckbox()}</div>
                        {renderButtonToolbar()}
                    </div>
                </div>
            </div>
        </div>
    );
};
